import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { BLOG_POSTS } from "../blogData";
import bHero from "../assets/blog/blog_hero_banner.png";

// Inline Icons for Premium UI
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3.5 h-3.5">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const TagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3.5 h-3.5">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" strokeWidth="3" />
  </svg>
);

const CommentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3.5 h-3.5">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const BlogListPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedTag, setSelectedTag] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 4;

  // Scroll to top on load
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedTag]);

  // Extract unique categories & tags
  const categories = ["All", ...new Set(BLOG_POSTS.map(p => p.category))];
  const tags = ["All", ...new Set(BLOG_POSTS.map(p => p.tag))];

  // Helper to count posts per category
  const getCategoryCount = (category) => {
    if (category === "All") return BLOG_POSTS.length;
    return BLOG_POSTS.filter(p => p.category === category).length;
  };

  // Filter posts
  const filteredPosts = BLOG_POSTS.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    const matchesTag = selectedTag === "All" || post.tag === selectedTag;
    return matchesSearch && matchesCategory && matchesTag;
  });

  // Sort by date (most recent first) - in this mock, we can just use ID desc or keep default array order
  const sortedPosts = [...filteredPosts].sort((a, b) => b.id - a.id);

  // Pagination logic
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = sortedPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(sortedPosts.length / postsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
  };

  return (
    <div className="rest-page">
      <Header />
      
      {/* Blog Page Banner */}
      <section 
        className="rest-blog-banner" 
        style={{ backgroundImage: `url(${bHero})` }}
      >
        <div className="max-w-7xl mx-auto">
          <h1>Crispy News & Stories</h1>
          <div className="rest-blog-breadcrumbs">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>Blog</span>
          </div>
        </div>
      </section>

      {/* Main Container */}
      <main className="rest-blog-container">
        <div className="rest-blog-layout">
          
          {/* Main Grid Column */}
          <div className="rest-blog-main-content">
            {currentPosts.length > 0 ? (
              <div className="rest-blog-grid">
                {currentPosts.map((post) => (
                  <article key={post.id} className="rest-blog-card">
                    {/* Card Image */}
                    <div className="rest-blog-card-img-wrap">
                      <span className="rest-blog-card-category-badge">
                        {post.category}
                      </span>
                      <img 
                        src={post.imgUrl} 
                        alt={post.title} 
                        className="rest-blog-card-img" 
                      />
                      <div className="rest-blog-card-date-badge">
                        <span className="rest-blog-card-date-day">{post.day}</span>
                        <span className="rest-blog-card-date-month">{post.month}</span>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="rest-blog-card-content">
                      <div className="rest-blog-card-meta">
                        <span>
                          <UserIcon />
                          By {post.author}
                        </span>
                        <span>
                          <TagIcon />
                          {post.tag}
                        </span>
                        <span>
                          <CommentIcon />
                          {post.commentsCount} Comments
                        </span>
                      </div>

                      <h2 className="rest-blog-card-title">
                        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                      </h2>

                      <p className="rest-blog-card-excerpt">{post.excerpt}</p>

                      <Link to={`/blog/${post.slug}`} className="rest-blog-card-readmore">
                        Read More
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="14" height="14">
                          <line x1="5" y1="12" x2="19" y2="12" />
                          <polyline points="12 5 19 12 12 19" />
                        </svg>
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-[#faf8f5] rounded-lg border border-dashed border-neutral-200">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-16 h-16 mx-auto text-neutral-400 mb-4">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
                <h3 className="text-lg font-bold text-neutral-800 mb-2">No Stories Found</h3>
                <p className="text-neutral-500 mb-6 max-w-sm mx-auto">We couldn't find any articles matching your search query or selected filters.</p>
                <button 
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("All");
                    setSelectedTag("All");
                  }}
                  className="rest-reply-submit-btn"
                >
                  Clear Filters
                </button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="rest-pagination">
                <button 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="rest-page-btn"
                  aria-label="Previous Page"
                >
                  &lt;
                </button>
                
                {Array.from({ length: totalPages }, (_, idx) => (
                  <button
                    key={idx + 1}
                    onClick={() => handlePageChange(idx + 1)}
                    className={`rest-page-btn${currentPage === idx + 1 ? " active" : ""}`}
                  >
                    {idx + 1}
                  </button>
                ))}

                <button 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="rest-page-btn"
                  aria-label="Next Page"
                >
                  &gt;
                </button>
              </div>
            )}
          </div>

          {/* Sidebar Column */}
          <aside className="rest-blog-sidebar">
            
            {/* Search Widget */}
            <div className="rest-sidebar-widget">
              <h3 className="rest-widget-title">Search</h3>
              <form onSubmit={handleSearchSubmit} className="rest-search-form">
                <input 
                  type="text" 
                  className="rest-search-input" 
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="rest-search-submit" aria-label="Search">
                  <SearchIcon />
                </button>
              </form>
            </div>

            {/* Categories Widget */}
            <div className="rest-sidebar-widget">
              <h3 className="rest-widget-title">Categories</h3>
              <ul className="rest-category-list">
                {categories.map((cat) => (
                  <li 
                    key={cat}
                    className={`rest-category-item${selectedCategory === cat ? " active" : ""}`}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    <span>{cat}</span>
                    <span className="rest-category-count">{getCategoryCount(cat)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Recent Posts Widget */}
            <div className="rest-sidebar-widget">
              <h3 className="rest-widget-title">Recent Posts</h3>
              <div className="rest-recent-post-list">
                {BLOG_POSTS.slice(0, 3).map((post) => (
                  <div key={post.id} className="rest-recent-post-item">
                    <img 
                      src={post.imgUrl} 
                      alt={post.title} 
                      className="rest-recent-post-img"
                    />
                    <div className="rest-recent-post-info">
                      <span className="rest-recent-post-date">{post.day} {post.month} {post.year}</span>
                      <h4 className="rest-recent-post-title">
                        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                      </h4>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tags Widget */}
            <div className="rest-sidebar-widget">
              <h3 className="rest-widget-title">Tags</h3>
              <div className="rest-tags-cloud">
                {tags.map((tag) => (
                  <button
                    key={tag}
                    className={`rest-tag-pill${selectedTag === tag ? " active" : ""}`}
                    onClick={() => setSelectedTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

          </aside>

        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogListPage;
